import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Landmark, Truck, Package, FileText, AlertTriangle, Trash2 } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import PetanqueLoader from '../components/PetanqueLoader';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../api/sangly';
import { timeAgo } from '../lib/format';

/**
 * Encodage — la configuration qui pilote l'encodage automatique des factures
 * fournisseurs dans Odoo : un profil par fournisseur + les règles générales.
 * Les routines la lisent à l'exécution via /api/encoding/profile/<partner_id>/.
 */

const REGIMES = {
  intracom: { label: 'Intracom', variant: 'default' },
  domestic_be: { label: 'Belge', variant: 'success' },
  service: { label: 'Services', variant: 'warning' },
  non_eu: { label: 'Hors UE', variant: 'info' },
};
const LINE_STYLES = {
  single_summed: 'Ligne unique (total) — obsolète',
  itemized: 'Lignes détaillées',
};

const MODEL_LABELS = { supplier: 'Fournisseur', rule: 'Règle', reference: 'Référence Odoo' };
const ACTION_LABELS = { create: 'création', delete: 'suppression' };

const EMPTY_SUPPLIER = {
  partner_id: '', display_name: '', country: '', vat: '', regime: 'intracom',
  payment_term_label: '', payment_term_odoo_id: null,
  goods_account_code: '', goods_account_odoo_id: null, goods_tax_label: '', goods_tax_odoo_id: null,
  transport_account_code: '', transport_account_odoo_id: null, transport_tax_label: '', transport_tax_odoo_id: null,
  iban: '', partner_bank_odoo_id: null, bank_secured: false, factoring_note: '',
  po_reference_location: '', payment_reference_rule: '', line_style: 'itemized',
  is_active: true, remarks: '',
};

// dropdown fed by the Odoo reference map (api/encoding/references); falls back to a free-text pair
function RefSelect({ label, kind, references, idValue, labelValue, codeValue, onPick }) {
  const options = references.filter((r) => r.kind === kind);
  const current = options.find((r) => r.odoo_id === idValue);
  if (!options.length) {
    return (
      <div className="space-y-1.5">
        <Label>{label}</Label>
        <div className="flex gap-2">
          <Input placeholder="Libellé" value={labelValue ?? codeValue ?? ''} onChange={(e) => onPick({ label: e.target.value })} />
          <Input placeholder="ID Odoo" type="number" className="w-28" value={idValue ?? ''}
                 onChange={(e) => onPick({ odoo_id: e.target.value === '' ? null : Number(e.target.value) })} />
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={current ? String(current.odoo_id) : ''}
              onChange={(e) => {
                const picked = options.find((r) => String(r.odoo_id) === e.target.value);
                onPick(picked ? { odoo_id: picked.odoo_id, label: picked.label, code: picked.code } : { odoo_id: null, label: '', code: '' });
              }}>
        <option value="">— aucun —</option>
        {options.map((r) => (
          <option key={r.odoo_id} value={r.odoo_id}>
            {r.code ? `${r.code} — ` : ''}{r.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

// "Supprimer" → inline confirmation → onConfirm. Sits at the left of a dialog footer.
function DeleteControl({ label, onConfirm, busy }) {
  const [armed, setArmed] = useState(false);
  if (!armed) {
    return (
      <Button variant="ghost" className="mr-auto text-destructive hover:text-destructive" onClick={() => setArmed(true)}>
        <Trash2 className="mr-1.5 h-4 w-4" /> Supprimer
      </Button>
    );
  }
  return (
    <div className="mr-auto flex items-center gap-2">
      <span className="text-sm text-destructive">{label}</span>
      <Button variant="destructive" size="sm" onClick={onConfirm} disabled={busy}>
        {busy ? 'Suppression…' : 'Oui, supprimer'}
      </Button>
      <Button variant="outline" size="sm" onClick={() => setArmed(false)} disabled={busy}>Non</Button>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {title}
      </p>
      {children}
    </div>
  );
}

function SupplierDialog({ open, onClose, supplier, references, onSaved }) {
  const isNew = !supplier?.id;
  const [form, setForm] = useState(EMPTY_SUPPLIER);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setForm({ ...EMPTY_SUPPLIER, ...(supplier || {}) }); setError(''); }
  }, [open, supplier]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const remove = async () => {
    setSaving(true); setError('');
    try {
      await api.encoding.deleteSupplier(supplier.partner_id);
      onSaved(null);
      onClose();
    } catch {
      setError('Échec de la suppression.');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const body = { ...form, partner_id: Number(form.partner_id) };
      const saved = isNew
        ? await api.encoding.createSupplier(body)
        : await api.encoding.updateSupplier(supplier.partner_id, body);
      onSaved(saved);
      onClose();
    } catch (e) {
      const data = e?.response?.data;
      setError(typeof data === 'object' ? JSON.stringify(data) : 'Échec de l’enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="max-w-3xl">
      <DialogHeader>
        <DialogTitle>{isNew ? 'Nouveau fournisseur' : `Modifier — ${supplier?.display_name}`}</DialogTitle>
      </DialogHeader>
      <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
        <Section icon={FileText} title="Identité">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Nom affiché</Label>
              <Input value={form.display_name} onChange={(e) => set({ display_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Partner ID Odoo</Label>
              <Input type="number" value={form.partner_id ?? ''} disabled={!isNew}
                     onChange={(e) => set({ partner_id: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Pays (ISO)</Label>
              <Input maxLength={2} placeholder="BE" value={form.country}
                     onChange={(e) => set({ country: e.target.value.toUpperCase() })} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>N° TVA</Label>
              <Input value={form.vat} onChange={(e) => set({ vat: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Régime</Label>
              <Select value={form.regime} onChange={(e) => set({ regime: e.target.value })}>
                {Object.entries(REGIMES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </Select>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <Switch checked={form.is_active} onCheckedChange={(v) => set({ is_active: v })} />
              <span className="text-sm">Actif</span>
            </div>
          </div>
        </Section>

        <Section icon={Package} title="Comptabilité — marchandises">
          <div className="grid gap-3 sm:grid-cols-2">
            <RefSelect label="Compte marchandises" kind="account" references={references}
                       idValue={form.goods_account_odoo_id} codeValue={form.goods_account_code}
                       onPick={(p) => set({
                         ...(p.odoo_id !== undefined && { goods_account_odoo_id: p.odoo_id }),
                         ...(p.code !== undefined && { goods_account_code: p.code }),
                         ...(p.label !== undefined && p.code === undefined && { goods_account_code: p.label }),
                       })} />
            <RefSelect label="TVA marchandises" kind="tax" references={references}
                       idValue={form.goods_tax_odoo_id} labelValue={form.goods_tax_label}
                       onPick={(p) => set({
                         ...(p.odoo_id !== undefined && { goods_tax_odoo_id: p.odoo_id }),
                         ...(p.label !== undefined && { goods_tax_label: p.label }),
                       })} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <RefSelect label="Condition de paiement" kind="payment_term" references={references}
                       idValue={form.payment_term_odoo_id} labelValue={form.payment_term_label}
                       onPick={(p) => set({
                         ...(p.odoo_id !== undefined && { payment_term_odoo_id: p.odoo_id }),
                         ...(p.label !== undefined && { payment_term_label: p.label }),
                       })} />
            <div className="space-y-1.5">
              <Label>Encodage des lignes</Label>
              <Select value={form.line_style} onChange={(e) => set({ line_style: e.target.value })}>
                {Object.entries(LINE_STYLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </div>
          </div>
        </Section>

        <Section icon={Truck} title="Comptabilité — transport">
          <div className="grid gap-3 sm:grid-cols-2">
            <RefSelect label="Compte transport" kind="account" references={references}
                       idValue={form.transport_account_odoo_id} codeValue={form.transport_account_code}
                       onPick={(p) => set({
                         ...(p.odoo_id !== undefined && { transport_account_odoo_id: p.odoo_id }),
                         ...(p.code !== undefined && { transport_account_code: p.code }),
                         ...(p.label !== undefined && p.code === undefined && { transport_account_code: p.label }),
                       })} />
            <RefSelect label="TVA transport" kind="tax" references={references}
                       idValue={form.transport_tax_odoo_id} labelValue={form.transport_tax_label}
                       onPick={(p) => set({
                         ...(p.odoo_id !== undefined && { transport_tax_odoo_id: p.odoo_id }),
                         ...(p.label !== undefined && { transport_tax_label: p.label }),
                       })} />
          </div>
        </Section>

        <Section icon={Landmark} title="Banque">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="col-span-2 space-y-1.5">
              <Label>IBAN</Label>
              <Input value={form.iban} onChange={(e) => set({ iban: e.target.value.replace(/\s/g, '') })} />
            </div>
            <div className="space-y-1.5">
              <Label>ID compte bancaire Odoo</Label>
              <Input type="number" value={form.partner_bank_odoo_id ?? ''}
                     onChange={(e) => set({ partner_bank_odoo_id: e.target.value === '' ? null : Number(e.target.value) })} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.bank_secured} onCheckedChange={(v) => set({ bank_secured: v })} />
            <span className="text-sm">IBAN sécurisé (vérifié dans Odoo)</span>
          </div>
          <div className="space-y-1.5">
            <Label>Note factoring</Label>
            <Textarea rows={2} value={form.factoring_note} onChange={(e) => set({ factoring_note: e.target.value })} />
          </div>
        </Section>

        <Section icon={FileText} title="Lecture du PDF">
          <div className="space-y-1.5">
            <Label>Où trouver la référence PO sur le PDF</Label>
            <Textarea rows={2} value={form.po_reference_location}
                      onChange={(e) => set({ po_reference_location: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Règle de référence de paiement</Label>
            <Textarea rows={2} value={form.payment_reference_rule}
                      onChange={(e) => set({ payment_reference_rule: e.target.value })} />
          </div>
        </Section>

        <Section icon={AlertTriangle} title="Remarques / alertes">
          <Textarea rows={3} placeholder="Particularités de ce fournisseur…"
                    value={form.remarks} onChange={(e) => set({ remarks: e.target.value })} />
        </Section>

        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
      <DialogFooter>
        {!isNew && (
          <DeleteControl busy={saving} onConfirm={remove}
                         label={`Supprimer le profil ${supplier?.display_name} ? La routine ne traitera plus ce fournisseur.`} />
        )}
        <Button variant="outline" onClick={onClose}>Annuler</Button>
        <Button onClick={save} disabled={saving || !form.display_name || !form.partner_id}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function RuleDialog({ open, onClose, rule, onSaved }) {
  const isNew = !rule?.id;
  const [form, setForm] = useState({ code: '', title: '', body: '', order: 0, is_active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setForm({ code: '', title: '', body: '', order: 0, is_active: true, ...(rule || {}) }); setError(''); }
  }, [open, rule]);

  const removeRule = async () => {
    setSaving(true); setError('');
    try {
      await api.encoding.deleteRule(rule.code);
      onSaved(null);
      onClose();
    } catch {
      setError('Échec de la suppression.');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const saved = isNew
        ? await api.encoding.createRule(form)
        : await api.encoding.updateRule(rule.code, form);
      onSaved(saved);
      onClose();
    } catch (e) {
      const data = e?.response?.data;
      setError(typeof data === 'object' ? JSON.stringify(data) : 'Échec de l’enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{isNew ? 'Nouvelle règle générale' : `Modifier — ${rule?.title}`}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Code (slug)</Label>
            <Input value={form.code} disabled={!isNew}
                   onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Titre</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Règle</Label>
          <Textarea rows={5} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} />
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Label>Ordre</Label>
            <Input type="number" className="w-20" value={form.order}
                   onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value || 0) }))} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
            <span className="text-sm">Active</span>
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
      <DialogFooter>
        {!isNew && (
          <DeleteControl busy={saving} onConfirm={removeRule}
                         label={`Supprimer la règle « ${rule.code} » ?`} />
        )}
        <Button variant="outline" onClick={onClose}>Annuler</Button>
        <Button onClick={save} disabled={saving || !form.code || !form.title || !form.body}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export default function Encodage() {
  const [tab, setTab] = useState('suppliers');
  const [suppliers, setSuppliers] = useState(null);
  const [rules, setRules] = useState(null);
  const [references, setReferences] = useState([]);
  const [changelog, setChangelog] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);   // null | {} (new) | supplier
  const [editRule, setEditRule] = useState(null);

  const reload = useCallback(() => {
    api.encoding.suppliers().then(setSuppliers).catch(() => setSuppliers([]));
    api.encoding.rules().then(setRules).catch(() => setRules([]));
    api.encoding.references().then(setReferences).catch(() => setReferences([]));
  }, []);
  useEffect(reload, [reload]);

  useEffect(() => {
    if (tab === 'history' && changelog === null) {
      api.encoding.changelog({ limit: 100 })
        .then((d) => setChangelog(d.results || d))
        .catch(() => setChangelog([]));
    }
  }, [tab, changelog]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (suppliers || [])
      .filter((s) => showInactive || s.is_active)
      .filter((s) => !q || s.display_name.toLowerCase().includes(q)
        || (s.vat || '').toLowerCase().includes(q) || String(s.partner_id).includes(q));
  }, [suppliers, search, showInactive]);

  const loading = suppliers === null || rules === null;

  return (
    <AppLayout title="Encodage" description="Profils fournisseurs et règles pour l'encodage automatique des factures dans Odoo.">
      {loading ? <PetanqueLoader /> : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="suppliers">Fournisseurs ({(suppliers || []).length})</TabsTrigger>
            <TabsTrigger value="rules">Règles générales ({(rules || []).length})</TabsTrigger>
            <TabsTrigger value="history">Historique</TabsTrigger>
          </TabsList>

          <TabsContent value="suppliers">
            <div className="mb-3 mt-4 flex flex-wrap items-center gap-3">
              <Input placeholder="Rechercher (nom, TVA, partner id)…" className="w-72"
                     value={search} onChange={(e) => setSearch(e.target.value)} />
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Switch checked={showInactive} onCheckedChange={setShowInactive} /> Afficher les inactifs
              </label>
              <div className="flex-1" />
              <Button onClick={() => setEditSupplier({})}>
                <Plus className="mr-1.5 h-4 w-4" /> Nouveau fournisseur
              </Button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fournisseur</TableHead>
                    <TableHead>Régime</TableHead>
                    <TableHead>Compte</TableHead>
                    <TableHead>TVA</TableHead>
                    <TableHead>Paiement</TableHead>
                    <TableHead>Banque</TableHead>
                    <TableHead>Lignes</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s) => (
                    <TableRow key={s.partner_id} className="cursor-pointer" onClick={() => setEditSupplier(s)}>
                      <TableCell>
                        <div className="font-medium">
                          {s.display_name}
                          {!s.is_active && <Badge variant="default" className="ml-2">Inactif</Badge>}
                          {s.remarks && <AlertTriangle className="ml-1.5 inline h-3.5 w-3.5 text-amber-500" title={s.remarks} />}
                        </div>
                        <div className="text-xs text-muted-foreground">#{s.partner_id}{s.vat ? ` · ${s.vat}` : ''}{s.country ? ` · ${s.country}` : ''}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={REGIMES[s.regime]?.variant || 'default'}>{REGIMES[s.regime]?.label || s.regime}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{s.goods_account_code || '—'}</TableCell>
                      <TableCell className="text-xs">{s.goods_tax_label || '—'}</TableCell>
                      <TableCell className="text-xs">{s.payment_term_label || '—'}</TableCell>
                      <TableCell className="text-xs">
                        {s.iban ? (
                          <span className="font-mono">{s.iban.slice(0, 8)}…{s.bank_secured && <Badge variant="success" className="ml-1.5">Sécurisé</Badge>}</span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-xs">{LINE_STYLES[s.line_style] || s.line_style}</TableCell>
                      <TableCell><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></TableCell>
                    </TableRow>
                  ))}
                  {!filtered.length && (
                    <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                      Aucun fournisseur. Ajoutez-en un via la skill <code>/onboard-supplier</code> (Claude Code) ou créez-en un ici.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="rules">
            <div className="mb-3 mt-4 flex justify-end">
              <Button onClick={() => setEditRule({})}>
                <Plus className="mr-1.5 h-4 w-4" /> Nouvelle règle
              </Button>
            </div>
            <div className="space-y-2">
              {(rules || []).map((r) => (
                <button key={r.code} onClick={() => setEditRule(r)}
                        className="flex w-full items-start gap-3 rounded-lg border border-border bg-card p-4 text-left hover:bg-muted/50">
                  <span className="mt-0.5 font-mono text-xs text-muted-foreground">{r.order}</span>
                  <span className="flex-1">
                    <span className="block font-medium">
                      {r.title}
                      {!r.is_active && <Badge variant="default" className="ml-2">Inactive</Badge>}
                    </span>
                    <span className="mt-0.5 block whitespace-pre-wrap text-sm text-muted-foreground">{r.body}</span>
                  </span>
                  <Pencil className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                </button>
              ))}
              {!(rules || []).length && (
                <p className="py-8 text-center text-sm text-muted-foreground">Aucune règle générale.</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="history">
            <div className="mt-4 overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quand</TableHead>
                    <TableHead>Qui</TableHead>
                    <TableHead>Quoi</TableHead>
                    <TableHead>Modifications</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(changelog || []).map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{timeAgo(c.changed_at)}</TableCell>
                      <TableCell className="text-sm">{c.changed_by_name || c.changed_by_email || '—'}</TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="default" className="mr-1.5">{MODEL_LABELS[c.model] || c.model}</Badge>
                        {c.object_label || c.object_key}
                        {c.action !== 'update' && <span className="ml-1.5 text-xs text-muted-foreground">({ACTION_LABELS[c.action] || c.action})</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {Object.entries(c.diff || {}).map(([field, d]) => (
                          <div key={field} className="text-muted-foreground">
                            <span className="font-mono">{field}</span>: {String(d.old ?? '∅')} → <span className="text-foreground">{String(d.new ?? '∅')}</span>
                          </div>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                  {changelog !== null && !changelog.length && (
                    <TableRow><TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">Aucune modification enregistrée.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      )}

      <SupplierDialog open={editSupplier !== null} onClose={() => setEditSupplier(null)}
                      supplier={editSupplier} references={references}
                      onSaved={() => reload()} />
      <RuleDialog open={editRule !== null} onClose={() => setEditRule(null)}
                  rule={editRule} onSaved={() => reload()} />
    </AppLayout>
  );
}
